import { HighlightedPhrase } from '@openx/components-core/lib/HighlightedPhrase/HighlightedPhrase.tsx';
import type { TableColumn } from '@openx/types/tableHelpers.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetAudiencesListQuery } from '../../../../graphql/query/audiences/GetAudiencesList.generated.tsx';
import { DEFAULT_PROVIDER_NAME, useProviders } from '../../../../modules/audiences/hooks/useProviders.ts';
import { DateCell } from '../components/cells/Date.tsx';
import { NameCell } from '../components/cells/Name.tsx';
import { PriceCell } from '../components/cells/Price.tsx';
import { ReachCell } from '../components/cells/Reach.tsx';
import { StatusCell } from '../components/cells/Status.tsx';
import { mapStatus } from '../utils/mapStatus.ts';

export const useTableColumns = (searchPhrase?: string) => {
  const { t } = useTranslation();
  const { getProviderName } = useProviders();

  return useMemo<TableColumn<GetAudiencesListQuery['audience'][number]>[]>(
    () => [
      {
        title: t('Name'),
        key: 'name',
        width: '32%',
        disabled: true,
        render: audience => <NameCell audience={audience} searchPhrase={searchPhrase ?? ''} />,
      },
      {
        title: t('Status'),
        key: 'status',
        width: '10%',
        render: ({ status: itemStatus, last_export }) => {
          const status = mapStatus(itemStatus);

          if (!status) {
            return null;
          }

          return <StatusCell label={status.label} status={status.status} lastExport={last_export} />;
        },
      },
      {
        title: t('Reach'),
        key: 'addressable_reach',
        width: '10%',
        render: audience => <ReachCell audience={audience} />,
      },
      {
        key: 'price',
        render: ({ pricing }) => <PriceCell pricing={pricing} />,
        title: t('Price'),
        width: '10%',
      },
      {
        title: t('Data Partner'),
        key: 'account',
        width: '15%',
        render: ({ account }) =>
          account ? <HighlightedPhrase searchPhrase={searchPhrase}>{account.name}</HighlightedPhrase> : null,
      },
      {
        title: t('Data Provider'),
        key: 'direct_audience_provider',
        width: '10%',
        render: ({ direct_audience_provider }) => (
          <HighlightedPhrase searchPhrase={searchPhrase}>
            {getProviderName(direct_audience_provider) || DEFAULT_PROVIDER_NAME}
          </HighlightedPhrase>
        ),
      },
      {
        title: t('Last Modified'),
        key: 'updated_date',
        width: '13%',
        render: ({ updated_date }) => <DateCell date={updated_date} />,
      },
    ],
    [t, getProviderName, searchPhrase]
  );
};
